import { CloseCircleTwoTone, PlusCircleTwoTone } from "@ant-design/icons";
import { Button, Col, DatePicker, Input, InputNumber, Row, Table, Typography, Form } from "antd";
import FacilitiesSelect from "components/FacilitiesSelect";
import { FacilityOption } from "components/FacilitiesSelect/FacilitiesSelect";
import colors from "css/colors";
import OrganizationsSearchbar from "pages/warehouse/LogHoursPage/Searchbars/OrganizationsSearchbar";
import React from "react";
import { ManualStockEntryStockType } from "services/BillingEngineService/interfaces/entities/ManualStockEntry";
import StockTypeSelect from "../StockTypeSelect";
import moment from "moment";

interface UpdateStockEntriesForm {
  selectedFacility?: FacilityOption;
  onFacilitySelect: (selectedFacility: FacilityOption) => void;
  selectedStockType?: ManualStockEntryStockType;
  onStockTypeSelect: (selectedStockType: ManualStockEntryStockType) => void;
  error: any;
  entries: any[];
  setEntries: any;
  addRow: any;
  removeRow: any;
  updateRow: any;
  hasSubmitted: boolean;
}

function UpdateStockEntriesForm({
  selectedFacility,
  onFacilitySelect,
  selectedStockType,
  onStockTypeSelect,
  error,
  entries,
  addRow,
  updateRow,
  removeRow,
  hasSubmitted,
}: UpdateStockEntriesForm) {
  const today = React.useRef(new Date()).current;

  return (
    <div>
      <Row gutter={30} justify="space-between">
        <Col span={6}>
          <Row align="middle" gutter={10}>
            <Col>
              <Typography.Title level={5} style={{ margin: 0 }}>
                Facility:
              </Typography.Title>
            </Col>
            <Col flex={1}>
              <Form.Item
                noStyle={hasSubmitted === false || selectedFacility !== undefined}
                help="Please select a facility"
                validateStatus="error"
              >
                <FacilitiesSelect selectedValue={selectedFacility?.name} onSelect={onFacilitySelect} />
              </Form.Item>
            </Col>
          </Row>
        </Col>

        <Col span={6}>
          <Row align="middle" gutter={10}>
            <Col>
              <Typography.Title level={5} style={{ margin: 0 }}>
                Date:
              </Typography.Title>
            </Col>
            <Col flex={1}>
              <DatePicker
                style={{ width: "100%" }}
                disabled={true}
                value={moment()}
                disabledDate={(date) => date.startOf("day").isAfter(today)}
              />
            </Col>
          </Row>
        </Col>

        <Col span={6}>
          <Row align="middle" gutter={10}>
            <Col>
              <Typography.Title level={5} style={{ margin: 0 }}>
                Stock Type:
              </Typography.Title>
            </Col>
            <Col flex={1}>
              <Form.Item
                noStyle={hasSubmitted === false || selectedStockType !== undefined}
                help="Please select a facility"
                validateStatus="error"
              >
                <StockTypeSelect value={selectedStockType} onSelect={onStockTypeSelect} />
              </Form.Item>
            </Col>
          </Row>
        </Col>
      </Row>

      <Row style={{ margin: "30px 0" }}>
        <Col>
          {error && (
            <Typography.Text style={{ color: colors.RED, fontWeight: "bold" }}>{`*${error?.message}`}</Typography.Text>
          )}
        </Col>
      </Row>

      <div>
        <UpdateStockTable removeRow={removeRow} updateRow={updateRow} entries={entries} />
        <Button
          type="text"
          onClick={() => {
            addRow();
          }}
          style={{
            fontSize: 30,
            height: "100%",
            margin: "15px auto",
            display: "block",
          }}
        >
          <PlusCircleTwoTone twoToneColor={colors.GREEN} />
        </Button>
      </div>
    </div>
  );
}

function UpdateStockTable({ entries = [], removeRow, updateRow }: any) {
  const columns = [
    {
      title: "Organization",
      dataIndex: ["organization", "name"],
      width: "30%",
      render: (_: any, record: any, index: number) => {
        const hasError = record.errors.includes("organization");
        return (
          <Form.Item
            noStyle={!hasError}
            help={hasError ? "Please select an organization" : ""}
            validateStatus={hasError ? "error" : "validating"}
          >
            <OrganizationsSearchbar
              style={{ width: "100%" }}
              value={record.organization}
              onSelect={(organization) => updateRow({ organization }, record.id)}
            />
          </Form.Item>
        );
      },
    },
    {
      title: "Stock",
      dataIndex: ["stockAmount"],
      width: "15%",
      render: (_: any, record: any) => {
        const hasError = record.errors.includes("stockAmount");

        return (
          <Form.Item
            noStyle={!hasError}
            help={hasError ? "Please select a stock amount" : ""}
            validateStatus={hasError ? "error" : "validating"}
          >
            <InputNumber
              style={{ width: "100%" }}
              min={0}
              value={record.stockAmount}
              onChange={(stockAmount) => updateRow({ stockAmount }, record.id)}
            />
          </Form.Item>
        );
      },
    },

    {
      title: "Notes",
      dataIndex: ["notes"],
      render: (_: any, record: any) => {
        return (
          <Input
            placeholder={`(Optional)`}
            onChange={(e) => updateRow({ notes: e.target.value }, record.id)}
            value={record.notes}
          />
        );
      },
    },

    {
      key: "action",
      width: "10%",
      render: (_: any, record: any) => {
        return (
          <Button type="text" onClick={() => removeRow(record.id)} style={{ fontSize: 16, height: "100%" }}>
            <CloseCircleTwoTone twoToneColor={colors.RED} />
          </Button>
        );
      },
    },
  ];

  return (
    <Table
      style={{ margin: "30px 0" }}
      dataSource={entries}
      rowKey={(record) => `${record.id}`}
      columns={columns}
      pagination={false}
    />
  );
}

export default UpdateStockEntriesForm;
